import * as THREE from 'three'
import { type TransformControls } from 'three/examples/jsm/controls/TransformControls.js'
import type { CustomViewHelper } from './lib/CustomViewHelper.ts'

import tippy from 'tippy.js'
import './environment.js'
import 'tippy.js/dist/tippy.css' // optional for styling

import { Utility } from './lib/Utilities.ts'
import { Generators } from './lib/Generators.ts'
import { Viewport } from './lib/Viewport.ts'
import { mount_engine_panels } from './lib/EnginePanels.ts'

import { UI } from './lib/UI.ts'

import { StepLoadModel } from './lib/processes/load-model/StepLoadModel.ts'
import { StepLoadSkeleton } from './lib/processes/load-skeleton/StepLoadSkeleton.ts'
import { StepEditSkeleton } from './lib/processes/edit-skeleton/StepEditSkeleton.ts'
import { MeshDragBonePlacement } from './lib/processes/edit-skeleton/MeshDragBonePlacement.ts'
import { StepBuildSkeleton } from './lib/processes/build-skeleton/StepBuildSkeleton.ts'
import { BuildSkeletonPanel } from './lib/processes/build-skeleton/BuildSkeletonPanel.ts'
import { type SkeletonDescriptor } from './features/skeleton/SkeletonDescriptor.ts'
import {
  ProjectSessionStorage,
  generate_session_id,
  type ProjectSession
} from './features/persistence/ProjectSessionStorage.ts'
import { type VertexInfluenceInspector } from './features/rig-quality/VertexInfluenceInspector.ts'
import { type LiveRigQualityPanel } from './features/rig-quality/LiveRigQualityPanel.ts'
import { AnimationLayerManager } from './features/timeline/AnimationLayerManager.ts'
import { type WeightPaintTool } from './features/timeline/WeightPaintTool.ts'
import { StepAnimationsListing } from './lib/processes/animations-listing/StepAnimationsListing.ts'
import { DownloadSettings } from './lib/processes/export-to-file/DownloadSettings.ts'
import { StepExportToFile } from './lib/processes/export-to-file/StepExportToFile.ts'
import { StepWeightSkin } from './lib/processes/weight-skin/StepWeightSkin.ts'

import { ProcessStep } from './lib/enums/ProcessStep.ts'
import { type Bone, Group, Scene, type Skeleton, type Vector3 } from 'three'

import { SkeletonType } from './lib/enums/SkeletonType.ts'
import { RigConfig } from './lib/RigConfig.ts'

import { CustomSkeletonHelper } from './lib/CustomSkeletonHelper.ts'
import { EventListeners } from './lib/EventListeners.ts'
import { ModelPreviewDisplay } from './lib/enums/ModelPreviewDisplay.ts'
import { TransformControlType } from './lib/enums/TransformControlType.ts'
import { TransformSpace } from './lib/enums/TransformSpace.ts'
import { ThemeManager } from './lib/ThemeManager.ts'
import { SettingsDropdownManager } from './lib/SettingsDropdownManager.ts'
import { ModalDialog } from './lib/ModalDialog.ts'
import { ModelCleanupUtility } from './lib/processes/load-model/ModelCleanupUtility.ts'

export class Mesh2MotionEngine extends EventTarget {
  public readonly viewport: Viewport = new Viewport()
  public readonly camera = this.viewport.camera
  public readonly renderer = this.viewport.renderer
  public readonly transform_controls: TransformControls = this.viewport.transform_controls

  public is_transform_controls_dragging: boolean = false
  public readonly transform_controls_hover_distance: number = 0.02 // distance to hover over bones to select them
  public is_model_gizmo_active: boolean = false
  public readonly mesh_drag_bone_placement: MeshDragBonePlacement

  // has UI elements on the HTML page that we will reference/use
  public readonly scene: Scene = this.viewport.scene
  public theme_manager: ThemeManager
  public settings_dropdown_manager: SettingsDropdownManager | undefined
  public ui: UI
  public load_model_step: StepLoadModel
  public load_skeleton_step: StepLoadSkeleton
  public build_skeleton_step: StepBuildSkeleton
  public build_skeleton_panel: BuildSkeletonPanel | undefined = undefined
  public edit_skeleton_step: StepEditSkeleton
  public weight_skin_step: StepWeightSkin
  public animations_listing_step: StepAnimationsListing
  public download_settings: DownloadSettings
  public file_export_step: StepExportToFile

  // for looking at specific bones
  public process_step: ProcessStep = ProcessStep.LoadModel
  public skeleton_helper: CustomSkeletonHelper | THREE.SkeletonHelper | undefined = undefined
  public use_custom_skeleton_helper: boolean = true // retargeting doesn't use this
  public debugging_visual_object: Group = new Group()

  // when editing the skeleton, what type of mesh will we see
  public mesh_preview_display_type: ModelPreviewDisplay = ModelPreviewDisplay.WeightPainted
  public transform_controls_type: TransformControlType = TransformControlType.Translation
  public transform_space_type: TransformSpace = TransformSpace.Global

  private readonly eventListeners: EventListeners

  // Tracking the in-flight project session (model bytes + step + skeleton).
  // Populated when the user uploads a model OR when they restore a saved
  // session. Each step transition / skeleton selection updates this entry in
  // IndexedDB via the debounced `schedule_session_save()` so an accidental
  // refresh can be recovered from "Resume project".
  private current_session: ProjectSession | undefined = undefined
  private session_save_handle: number | null = null

  public vertex_inspector: VertexInfluenceInspector | undefined = undefined
  public live_rig_quality_panel: LiveRigQualityPanel | undefined = undefined
  public animation_layer_manager: AnimationLayerManager = new AnimationLayerManager()
  public weight_paint_tool: WeightPaintTool | undefined = undefined

  constructor () {
    super()
    this.eventListeners = new EventListeners(this)

    this.theme_manager = new ThemeManager()
    this.ui = UI.getInstance()
    this.settings_dropdown_manager = undefined

    // setting up steps
    this.load_model_step = new StepLoadModel()
    this.load_skeleton_step = new StepLoadSkeleton(this.scene)
    this.build_skeleton_step = new StepBuildSkeleton({
      scene: this.scene,
      camera: this.camera,
      transform_controls: this.transform_controls,
      renderer_canvas: this.renderer.domElement
    })
    this.edit_skeleton_step = new StepEditSkeleton()
    this.weight_skin_step = new StepWeightSkin()
    this.animations_listing_step = new StepAnimationsListing(this.theme_manager)
    this.animations_listing_step.install_pose_mode_controller(this.transform_controls)
    this.animations_listing_step.set_renderer_canvas(this.renderer.domElement)
    this.animations_listing_step.set_scene(this.scene)
    this.animations_listing_step.set_export_clip_dispatcher((clip, filename) => {
      // Per-clip export: hand a single-clip array to the file exporter.
      this.file_export_step.set_animation_clips_to_export([clip], [0])
      const meshes = this.weight_skin_step.final_skinned_meshes()
      // Look up the clip's thumbnail (only user-authored clips have one).
      // If found, package GLB + PNG in a ZIP — handy for sharing previews
      // alongside the rig. Otherwise fall through to a raw GLB save.
      const pair = this.animations_listing_step.find_pair_by_clip?.(clip)
      const thumb = pair?.metadata?.thumbnail_data_url
      void this.file_export_step.export_with_thumbnail(meshes, filename, this.download_settings, thumb)
    })
    this.download_settings = new DownloadSettings()
    this.file_export_step = new StepExportToFile()
    this.mesh_drag_bone_placement = new MeshDragBonePlacement(
      this.camera,
      this.edit_skeleton_step,
      this.load_model_step,
      this.weight_skin_step,
      this.transform_controls_hover_distance
    )

    this.viewport.setup_environment(this.theme_manager, this.mesh_drag_bone_placement)
    this.settings_dropdown_manager = new SettingsDropdownManager(this.viewport.get_scene_environment())

    // Drive per-frame step updates through Viewport's frame-callback hook.
    this.viewport.on_frame((delta_time) => {
      if (this.process_step === ProcessStep.AnimationsListing) {
        this.animations_listing_step.frame_change(delta_time)
      }
    })

    this.eventListeners.addEventListeners()
    this.process_step = this.process_step_changed(ProcessStep.LoadModel)
    this.viewport.start()
    this.inject_build_version()
    this.setup_tooltips()
    this.setup_session_persistence()
    mount_engine_panels(this)
  }

  public get view_helper (): CustomViewHelper | undefined {
    return this.viewport.view_helper
  }


  /**
   * Wire the project-session autosave: capture model bytes when the user
   * uploads a file, refresh the session entry when the skeleton selection
   * changes, and (debounced) persist to IDB on every step transition. Step
   * autosave is triggered from `process_step_changed` directly.
   */
  private setup_session_persistence (): void {
    this.load_model_step.addEventListener('userUploadedFile', ((e: CustomEvent<{ file: File }>) => {
      void this.start_session_with_file(e.detail.file)
    }) as EventListener)

    this.load_skeleton_step.addEventListener('skeletonLoaded', () => {
      // The dropdown reflects the user's choice; cache it on the session.
      const select = document.querySelector<HTMLSelectElement>('#skeleton-selection')
      if (this.current_session !== undefined && select !== null) {
        this.current_session.skeleton_selection = select.value
        this.schedule_session_save()
      }
    })
  }

  /**
   * Start (or update) the session keyed to the file the user just uploaded.
   * Called from the engine's userUploadedFile listener; safe to call multiple
   * times with the same file (creates a fresh session each upload — uploading
   * a different model means a different project, not the same one).
   */
  private async start_session_with_file (file: File): Promise<void> {
    try {
      const bytes = await file.arrayBuffer()
      const ext = (file.name.split('.').pop() ?? '').toLowerCase()
      const now = new Date().toISOString()
      this.current_session = {
        id: generate_session_id(),
        name: file.name,
        created_at: now,
        updated_at: now,
        last_step: this.process_step,
        model: { bytes, extension: ext, file_name: file.name },
        skeleton_selection: ''
      }
      // Capture an initial blank thumbnail; it'll be replaced on the next
      // step transition when the model has rendered.
      await ProjectSessionStorage.put(this.current_session)
    } catch (err) {
      console.warn('Failed to capture session for uploaded file:', err)
    }
  }

  /**
   * Debounced IDB write for the current session. Step transitions and
   * skeleton selections call this freely; only the most recent state hits
   * disk after a short idle period (avoids hammering IDB on rapid clicks).
   */
  private schedule_session_save (): void {
    if (this.current_session === undefined) return
    if (this.session_save_handle !== null) {
      window.clearTimeout(this.session_save_handle)
    }
    this.session_save_handle = window.setTimeout(() => {
      this.session_save_handle = null
      if (this.current_session === undefined) return
      // Capture a thumbnail at save time so the resume list has a preview that
      // reflects the most recent state (rather than the initial blank scene).
      this.current_session.thumbnail_data_url = this.capture_canvas_thumbnail(192, 144)
      this.current_session.last_step = this.process_step
      void ProjectSessionStorage.put(this.current_session)
    }, 800)
  }

  public capture_canvas_thumbnail (width: number, height: number): string | undefined {
    return this.viewport.capture_canvas_thumbnail(width, height)
  }

  public get_current_session_id (): string | undefined {
    return this.current_session?.id
  }

  /**
   * Restore a saved session: reload the model from stored bytes and put the
   * user back at LoadSkeleton with their skeleton selection prefilled. Edit
   * state and weight tweaks aren't persisted in v1, so steps after
   * LoadSkeleton are left to the user to re-walk through.
   */
  public async restore_session (id: string): Promise<boolean> {
    const session = await ProjectSessionStorage.get(id)
    if (session === null) return false
    const { extension } = session.model
    if (extension !== 'glb' && extension !== 'fbx') {
      console.warn(`restore_session: only glb/fbx restoration is supported in v1; got "${extension}".`)
      return false
    }
    this.current_session = session
    // Build a blob URL the existing load pipeline can fetch (works for both
    // GLB through GLTFLoader and FBX through CustomFBXLoader).
    const blob = new Blob([session.model.bytes])
    const url = URL.createObjectURL(blob)
    return await new Promise<boolean>((resolve) => {
      const on_loaded = (): void => {
        this.load_model_step.removeEventListener('modelLoaded', on_loaded)
        // Advance past LoadModel to LoadSkeleton; the user picks up from there.
        this.process_step_changed(ProcessStep.LoadSkeleton)
        // Restore skeleton selection if any — defer one tick so the dropdown
        // (populated in StepLoadSkeleton.begin) exists.
        if (session.skeleton_selection.length > 0) {
          window.setTimeout(() => {
            const select = document.querySelector<HTMLSelectElement>('#skeleton-selection')
            if (select !== null && Array.from(select.options).some(o => o.value === session.skeleton_selection)) {
              select.value = session.skeleton_selection
              select.dispatchEvent(new Event('change'))
            }
          }, 0)
        }
        URL.revokeObjectURL(url)
        resolve(true)
      }
      this.load_model_step.addEventListener('modelLoaded', on_loaded)
      this.load_model_step.load_model_file(url, extension)
    })
  }

  public get_theme_manager(): ThemeManager {
    return this.theme_manager
  }

  /** Eventually make the scene its own singleton/manager class
   * that we can inject into other classes that need it
   */
  public get_scene (): Scene {
    return this.scene
  }

  /* Add this attribute to an HTML element to give it a tooltip */
  private setup_tooltips (): void {
    tippy('[data-tippy-content]', { theme: 'mesh2motion' })
  }

  // for the release, let's just show the first N characters of the commit SHA
  // then the branch we used to build. This comes from Cloudflare build process
  private inject_build_version (): void {
    if (this.ui.dom_build_version !== null) {
      const commit_sha: string = window.CLOUDFLARE_COMMIT_SHA.slice(0, 9)
      const branch: string = window.CLOUDFLARE_BRANCH

      this.ui.dom_build_version.innerHTML = `git:${commit_sha}-${branch}`
    }
  }

  public set_camera_position (position: Vector3): void {
    this.viewport.set_camera_position(position)
  }

  /** Trigger a gentle camera shake transition effect. */
  public shake_camera (intensity?: number, duration?: number): void {
    this.viewport.shake_camera(intensity, duration)
  }

  public set_zoom_limits (min_distance: number, max_distance: number): void {
    this.viewport.set_zoom_limits(min_distance, max_distance)
  }

  public set_custom_skeleton_helper_enabled (enabled: boolean): void {
    this.use_custom_skeleton_helper = enabled
  }

  public set_fog_enabled (enabled: boolean): void {
    this.viewport.set_fog_enabled(enabled)
  }

  public enable_orbit_controls (enabled: boolean): void {
    this.viewport.enable_orbit_controls(enabled)
  }

  public regenerate_floor_grid (): void {
    this.viewport.regenerate_floor_grid()
  }

  public regenerate_skeleton_helper (new_skeleton: Skeleton, helper_name = 'Skeleton Helper'): void {
    // if skeleton helper exists...remove it
    if (this.skeleton_helper !== undefined) {
      this.scene.remove(this.skeleton_helper)
    }

    if (this.use_custom_skeleton_helper) {
      this.skeleton_helper = new CustomSkeletonHelper(new_skeleton.bones[0], { linewidth: 4, color: 0x4e7d58 }) // line segment color
    } else {
      this.skeleton_helper = new THREE.SkeletonHelper(new_skeleton.bones[0])
    }

    this.skeleton_helper.name = helper_name
    this.scene.add(this.skeleton_helper)
  }

  public sync_skeleton_helper_joint_visibility (): void {
    if (!(this.skeleton_helper instanceof CustomSkeletonHelper)) {
      return
    }

    const is_edit_skeleton_step = this.process_step === ProcessStep.EditSkeleton

    this.skeleton_helper.setJointsVisible(is_edit_skeleton_step)
    this.skeleton_helper.setHideRightSideJoints(
      is_edit_skeleton_step && this.edit_skeleton_step.is_mirror_mode_enabled()
    )
  }

  public update_a_pose_options_visibility (): void {
    if (this.ui.dom_arm_extension_options != null) {
      const config = RigConfig.by_skeleton_type(this.load_skeleton_step.skeleton_type())
      if (config?.has_arm_extension === true) {
        this.ui.dom_arm_extension_options.style.display = 'block'
      } else {
        this.ui.dom_arm_extension_options.style.display = 'none'
      }
    }
  }

  public handle_transform_controls_moving (): void {
    if (this.is_model_gizmo_active) { return }

    const selected_bone: Bone = this.transform_controls.object as Bone

    if (this.edit_skeleton_step.is_mirror_mode_enabled()) {
      this.edit_skeleton_step.apply_mirror_mode(selected_bone, this.transform_controls.getMode())
    }

    if (this.edit_skeleton_step.independent_bone_movement.is_enabled() &&
        this.transform_controls.getMode() === 'translate') {
      const mirror_bone = this.edit_skeleton_step.is_mirror_mode_enabled()
        ? this.edit_skeleton_step.find_mirror_bone(selected_bone)
        : undefined
      this.edit_skeleton_step.independent_bone_movement.apply(selected_bone, mirror_bone)
    }
  }

  // --- Model position gizmo (step 2) ---

  public enable_model_gizmo (): void {
    this.is_model_gizmo_active = true
    this.transform_controls.attach(this.load_model_step.model_meshes())
    this.transform_controls.setMode('translate')
    this.transform_controls.enabled = true
  }

  private bake_and_disable_model_gizmo (): void {
    const mesh_data = this.load_model_step.model_meshes()
    const pos = mesh_data.position
    if (pos.x !== 0 || pos.y !== 0 || pos.z !== 0) {
      ModelCleanupUtility.translate_model_vertices(mesh_data, pos.x, pos.y, pos.z)
      mesh_data.position.set(0, 0, 0)
    }
    this.is_model_gizmo_active = false
    this.transform_controls.detach()
    this.transform_controls.enabled = false
  }

  public handle_mesh_drag_mode_mouse_down (mouse_event: MouseEvent): void {
    this.mesh_drag_bone_placement.handle_mouse_down(mouse_event)
  }

  public handle_mesh_drag_mode_mouse_move (mouse_event: MouseEvent): void {
    this.mesh_drag_bone_placement.handle_mouse_move(mouse_event)
  }

  public handle_mesh_drag_mode_mouse_up (): void {
    const did_end_drag = this.mesh_drag_bone_placement.handle_mouse_up()

    if (!did_end_drag) {
      return
    }

    if (this.process_step === ProcessStep.EditSkeleton &&
      this.mesh_preview_display_type === ModelPreviewDisplay.WeightPainted) {
      this.regenerate_weight_painted_preview_mesh()
    }
  }

  public update_edit_bone_interaction_mode (): void {
    this.mesh_drag_bone_placement.sync_interaction_mode(this.process_step, this.transform_controls)
    this.is_transform_controls_dragging = false
  }

  public get is_mesh_drag_mode_dragging (): boolean {
    return this.mesh_drag_bone_placement.is_dragging()
  }

  private show_skin_failure_message (bone_names_with_errors: string[], error_point_positions: Vector3[]): void {
    // add the bone vertices as X markers to debugging object
    const error_markers: Group = Generators.create_x_markers(error_point_positions, 0.02, 0xff0000)
    this.debugging_visual_object.add(error_markers)
  }

  private update_current_process_step (process_step: ProcessStep): void {
    this.process_step = process_step
  }

  // the retargeting functionality also uses, so expose this out publicly
  public show_animation_player (show: boolean): void {
    if (this.ui.dom_animation_player === null) {
      console.error('Cannot find animation player DOM element to show/hide')
      return
    }

    if (show) {
      this.ui.dom_animation_player.style.display = 'flex'
      return
    }

    this.ui.dom_animation_player.style.display = 'none'
  }

  /**
   * Dispatch table for step-specific entry logic. Each entry runs after the
   * shared pre-step cleanup. Adding a step is one new entry — not editing an
   * if/else chain.
   */
  // `WeightSkin` and `StepExportToFile` are tracked in `ProcessStep` because
  // persisted sessions can carry those labels, but the engine's step machine
  // doesn't drive them — they're effectively no-ops here.
  private readonly step_enter_handlers: Readonly<Record<ProcessStep, () => void>> = {
    [ProcessStep.LoadModel]: () => { this.enter_load_model() },
    [ProcessStep.LoadSkeleton]: () => { this.enter_load_skeleton() },
    [ProcessStep.BuildSkeleton]: () => { this.begin_build_skeleton_step() },
    [ProcessStep.EditSkeleton]: () => { this.enter_edit_skeleton() },
    [ProcessStep.BindPose]: () => { this.enter_bind_pose() },
    [ProcessStep.AnimationsListing]: () => { this.enter_animations_listing() },
    [ProcessStep.WeightSkin]: () => { /* no-op: not driven from the step machine */ },
    [ProcessStep.StepExportToFile]: () => { /* no-op: not driven from the step machine */ }
  }

  public process_step_changed (process_step: ProcessStep): ProcessStep {
    this.ui.hide_all_elements()
    this.update_current_process_step(process_step)
    this.cleanup_prior_step(process_step)

    this.step_enter_handlers[process_step]()

    // Persist session step transition (debounced) so "Resume project" lands
    // the user back at the right step after a refresh.
    this.schedule_session_save()

    return this.process_step
  }

  /**
   * Pre-step cleanup shared across every transition: dispose lingering step
   * state, tear down step-specific overlays (pose mode, mocap, onion skin),
   * bake the model gizmo if it was active, and detach transform controls.
   */
  private cleanup_prior_step (next_step: ProcessStep): void {
    this.edit_skeleton_step.cleanup_on_exit_step()
    this.load_skeleton_step.dispose()
    if (next_step !== ProcessStep.BuildSkeleton && this.build_skeleton_step.is_active()) {
      this.build_skeleton_step.dispose()
      this.build_skeleton_panel?.unmount()
    }

    if (next_step === ProcessStep.AnimationsListing) {
      this.show_animation_player(true)
    } else {
      this.show_animation_player(false)
      // navigating away from animation step — auto-exit pose mode and mocap so
      // they're not stuck on when the user comes back later (mocap also releases
      // the webcam stream, which the user definitely wants stopped on navigation).
      // Also tear down onion-skin ghosts: they hold cloned meshes + per-ghost
      // mixers that would otherwise leak across step transitions.
      this.animations_listing_step.exit_pose_mode()
      this.animations_listing_step.exit_mocap()
      this.animations_listing_step.disable_onion_skin()
    }

    if (this.is_model_gizmo_active) {
      this.bake_and_disable_model_gizmo()
    }

    // when we change steps, we are re-creating the skeleton and helper
    // so the current transform control reference will be lost/give an error
    this.transform_controls.detach()
  }

  private enter_load_model (): void {
    this.remove_imported_model()
    this.load_model_step.clear_loaded_model_data()
    this.load_model_step.begin()
  }

  private enter_load_skeleton (): void {
    if (this.skeleton_helper !== undefined) {
      this.scene.remove(this.skeleton_helper)
    }
    // Remove leftover scene objects from a previous model so switching models
    // in Explore mode doesn't leak ghost overlaps or hidden Imported Models.
    this.remove_imported_model()
    this.remove_skinned_meshes_from_scene()

    this.mesh_preview_display_type = ModelPreviewDisplay.Textured
    this.changed_model_preview_display(this.mesh_preview_display_type)

    this.scene.add(this.load_model_step.model_meshes())
    this.enable_model_gizmo()
    this.load_skeleton_step.begin()
  }

  private enter_edit_skeleton (): void {
    this.load_skeleton_step?.dispose()
    this.regenerate_skeleton_helper(this.edit_skeleton_step.skeleton())
    this.edit_skeleton_step.begin(this.scene, this.load_skeleton_step.skeleton_type())
    this.update_edit_bone_interaction_mode()
    this.transform_controls.setMode(this.transform_controls_type)
    this.sync_skeleton_helper_joint_visibility()

    this.mesh_preview_display_type = ModelPreviewDisplay.WeightPainted
    this.changed_model_preview_display(this.mesh_preview_display_type)
  }

  private enter_bind_pose (): void {
    this.transform_controls.enabled = false
    this.calculate_skin_weighting_for_models()
    this.remove_skinned_meshes_from_scene()
    this.scene.add(...this.weight_skin_step.final_skinned_meshes())
    this.weight_skin_step.weight_painted_mesh_group().visible = false
    // BindPose is a transient: weight calculation runs here, then we fall
    // through to AnimationsListing in the same tick.
    this.process_step_changed(ProcessStep.AnimationsListing)
  }

  private enter_animations_listing (): void {
    const active_skeleton_type: SkeletonType = this.load_skeleton_step.skeleton_type()
    this.animations_listing_step.begin(active_skeleton_type, this.load_skeleton_step.skeleton_scale())

    // download options for export currently will only work for humanoid skeletons
    // since we will give options to change the bone names to other standard formats.
    this.download_settings.update_download_settings_ui_visibility(active_skeleton_type)

    const binding_skeleton = this.weight_skin_step.skeleton()
    if (binding_skeleton !== undefined) {
      this.regenerate_skeleton_helper(binding_skeleton)
      this.sync_skeleton_helper_joint_visibility()
    }

    if (this.ui.dom_show_skeleton_checkbox !== null) {
      this.ui.dom_show_skeleton_checkbox.checked = false
    }

    this.update_a_pose_options_visibility()
    this.animations_listing_step.load_and_apply_default_animation_to_skinned_mesh(this.weight_skin_step.final_skinned_meshes())

    if (this.skeleton_helper !== undefined) {
      this.skeleton_helper.visible = false
    }
  }

  /**
   * Enter Build Skeleton mode. Source-mesh availability depends on whether the
   * user reached us from the inline LoadSkeleton entry (mesh present, can
   * raycast against it for bone placement) or from the standalone top-level
   * mode (no mesh, places bones against the floor plane).
   */
  public begin_build_skeleton_step (initial?: SkeletonDescriptor): void {
    if (this.ui.dom_build_skeleton_tools !== null) {
      this.ui.dom_build_skeleton_tools.style.display = 'flex'
    }
    if (this.is_model_gizmo_active) {
      this.bake_and_disable_model_gizmo()
    }

    const inline_mesh = this.weight_skin_step.final_skinned_meshes()[0]
    const mesh_for_raycast = inline_mesh
    const mode = mesh_for_raycast === undefined && !this.has_loaded_unrigged_mesh()
      ? 'standalone'
      : 'inline'

    this.build_skeleton_step.enter({
      mode,
      mesh: mesh_for_raycast,
      initial
    })

    if (this.ui.dom_build_skeleton_tools !== null && this.build_skeleton_panel === undefined) {
      this.build_skeleton_panel = new BuildSkeletonPanel({
        step: this.build_skeleton_step,
        root: this.ui.dom_build_skeleton_tools,
        on_save: (descriptor) => this.handle_build_skeleton_saved(descriptor),
        on_cancel: () => this.handle_build_skeleton_cancelled(),
        capture_thumbnail: (w, h) => this.capture_canvas_thumbnail(w, h)
      })
      this.build_skeleton_panel.mount()
    } else if (this.build_skeleton_panel !== undefined) {
      // Re-render from current state when re-entering.
      this.build_skeleton_panel.unmount()
      this.build_skeleton_panel.mount()
    }
  }

  private has_loaded_unrigged_mesh (): boolean {
    try {
      const meshes = this.load_model_step.model_meshes()
      return meshes !== undefined && meshes.children.length > 0
    } catch (_err) {
      return false
    }
  }

  private handle_build_skeleton_saved (descriptor: SkeletonDescriptor): void {
    this.build_skeleton_step.dispose()
    if (this.build_skeleton_panel !== undefined) {
      this.build_skeleton_panel.unmount()
    }
    this.dispatchEvent(new CustomEvent('build-skeleton-saved', { detail: descriptor }))
  }

  private handle_build_skeleton_cancelled (): void {
    this.build_skeleton_step.dispose()
    if (this.build_skeleton_panel !== undefined) {
      this.build_skeleton_panel.unmount()
    }
    this.dispatchEvent(new CustomEvent('build-skeleton-cancelled'))
  }

  public changed_model_preview_display (mesh_textured_display_type: ModelPreviewDisplay): void {
    this.mesh_preview_display_type = mesh_textured_display_type

    // show/hide loaded textured model depending on view
    this.load_model_step.model_meshes().visible = this.mesh_preview_display_type === ModelPreviewDisplay.Textured

    if (this.mesh_preview_display_type === ModelPreviewDisplay.WeightPainted) {
      this.regenerate_weight_painted_preview_mesh()
    }

    // show/hide weight painted mesh depending on view
    this.weight_skin_step.weight_painted_mesh_group().visible =
      this.mesh_preview_display_type === ModelPreviewDisplay.WeightPainted
  }

  public changed_transform_controls_mode (radio_button_selected: string): void {
    switch (radio_button_selected) {
      case 'translate':
        this.transform_controls_type = TransformControlType.Translation
        this.transform_controls.setMode('translate')
        break
      case 'rotation':
        this.transform_controls_type = TransformControlType.Rotation
        this.transform_controls.setMode('rotate')
        break
      default:
        console.warn(`Unknown transform mode selected: ${radio_button_selected}`)
        break
    }
  }

  public changed_transform_controls_space (radio_button_selected: TransformSpace | undefined): void {
    if (radio_button_selected) {
      this.transform_space_type = radio_button_selected
      this.transform_controls.setSpace(radio_button_selected as 'world' | 'local')
    } else {
      console.warn(`Unknown transform space selected`)
    }
  }

  public handle_transform_controls_mouse_down (mouse_event: MouseEvent): void {
    // primary click is made for rotating around 3d scene
    const is_primary_button_click = mouse_event.button === 0

    if (is_primary_button_click === false) { return }

    // when we are done with skinned mesh, we shouldn't be editing transforms
    if (!this.transform_controls.enabled) {
      return
    }

    // Pose Mode in the AnimationsListing step uses the live skinned mesh's skeleton
    // (instead of the EditSkeleton step's setup skeleton). Branch on which step is
    // currently driving the gizmo.
    const pose_mode_skeleton: Skeleton | null = (this.process_step === ProcessStep.AnimationsListing)
      ? this.animations_listing_step.get_pose_mode_raycast_skeleton()
      : null

    if (pose_mode_skeleton !== null) {
      this.handle_pose_mode_mouse_down(mouse_event, pose_mode_skeleton)
      return
    }

    if (this.edit_skeleton_step.skeleton()?.bones === undefined) { return }

    // we will change which skeleton we do an intersection test with
    // depending on what step we are on. We are either moving the setup skeleton
    // or moving the bind pose skeleton
    const skeleton_to_test: Skeleton | undefined = this.edit_skeleton_step.skeleton()

    // if no skeleton to test, abort
    if (skeleton_to_test === undefined) {
      console.warn('No skeleton to test for intersection, aborting transform controls mouse down')
      return
    }

    // this returns 3 values, so we can destructure them. do not remove any of these
    // even if one of them is not used, otherwise there will be weird issues
    const [closest_bone, , closest_distance] = Utility.raycast_closest_bone_test(this.camera, mouse_event, skeleton_to_test)

    // don't allow to select root bone for now
    if (closest_bone?.name === 'root') {
      return
    }

    if (!this.edit_skeleton_step.is_bone_selectable(closest_bone)) {
      return
    }

    // only do selection if we are close
    // the orbit controls also have panning with alt-click, so we don't want to interfere with that
    if (closest_distance === null || closest_distance > this.transform_controls_hover_distance) {
      return
    }

    if (closest_bone !== null) {
      this.transform_controls.attach(closest_bone)
      this.edit_skeleton_step.set_currently_selected_bone(closest_bone)
    } else {
      this.edit_skeleton_step.set_currently_selected_bone(null)
    }
  }

  /**
   * Pose Mode bone-pick: simpler than the EditSkeleton flow — no mirror filtering,
   * no setup-skeleton bookkeeping. Routes through `PoseModeController.select_bone`
   * so listeners (gizmo attach + weight highlighter + picker UI sync) all fire
   * from one place regardless of how the bone got selected.
   */
  private handle_pose_mode_mouse_down (mouse_event: MouseEvent, skeleton: Skeleton): void {
    const [closest_bone, , closest_distance] = Utility.raycast_closest_bone_test(this.camera, mouse_event, skeleton)
    if (closest_bone === null) return
    if (closest_bone.name === 'root') return
    if (closest_distance === null || closest_distance > this.transform_controls_hover_distance) return

    this.animations_listing_step.select_pose_mode_bone(closest_bone)
  }

  public remove_skinned_meshes_from_scene (): void {
    const existing_skinned_meshes = this.scene.children.filter((child: THREE.Object3D) => child.name.includes('Skinned Mesh'))
    existing_skinned_meshes.forEach((existing_skinned_mesh: THREE.Object3D) => {
      Utility.remove_object_with_children(existing_skinned_mesh)
    })
  }

  public remove_imported_model (): void {
    if (this.load_model_step.model_meshes() !== undefined) {
      const imported_model = this.scene.getObjectByName('Imported Model')
      if (imported_model !== undefined) {
        this.scene.remove(imported_model)
      }
    }
  }

  public remove_weight_painted_mesh_preview (): void {
    if (this.load_model_step.model_meshes() !== undefined) {
      const weight_painted_mesh = this.scene.getObjectByName('Weight Painted Mesh Preview')
      if (weight_painted_mesh !== undefined) {
        this.scene.remove(weight_painted_mesh)
      }
    }
  }

  public regenerate_weight_painted_preview_mesh (): void {
    // needed for skinning process
    this.calculate_skin_weighting_for_models()

    // if the weight painted mesh is not in scene, add it
    if (this.scene.getObjectByName('Weight Painted Mesh') === undefined) {
      this.scene.add(this.weight_skin_step.weight_painted_mesh_group())
    }
  }

  private calculate_skin_weighting_for_models (): void {
    // we only need one binding skeleton. All skinned meshes will use this.
    this.weight_skin_step.reset_all_skin_process_data() // clear out any existing skinned meshes in storage

    // needed for skinning process if we change modes
    this.weight_skin_step.create_bone_formula_object(this.edit_skeleton_step.armature(), this.load_skeleton_step.skeleton_type())

    // Pass head weight correction settings to the weight skin step
    this.weight_skin_step.set_head_weight_correction_settings(
      this.edit_skeleton_step.use_head_weight_correction(),
      this.edit_skeleton_step.get_preview_plane_height()
    )

    this.weight_skin_step.create_binding_skeleton()

    // add geometry data needed for skinning
    this.load_model_step.models_geometry_list().forEach((mesh_geometry) => {
      this.weight_skin_step.add_to_geometry_data_to_skin(mesh_geometry)
    })

    // all mesh material data associated with the geometry data
    this.load_model_step.models_material_list().forEach((mesh_material) => {
      this.weight_skin_step.add_mesh_material(mesh_material)
    })

    // perform skinning operation
    // this will take all the mesh geometry data we added above and create skinned meshes
    // TODO: Always regenerate the weight painted mesh preview for now. This will change later
    // when we have are in the "Weight Painted" display mode
    this.weight_skin_step.calculate_weights_for_all_mesh_data(true)

    // remember our skeleton position before we do the skinning process
    // that way if we revert to try again...we will have the original positions/rotations
    this.load_model_step.model_meshes().visible = false // hide our unskinned mesh after we have done the skinning process

    // re-define skeleton helper to use the skinned mesh)
    if (this.weight_skin_step.skeleton() === undefined) {
      console.warn('Tried to regenerate skeleton helper, but skeleton is undefined!')
    }
  }

  public setup_weight_skinning_config (): void {
    this.weight_skin_step.create_bone_formula_object(this.edit_skeleton_step.armature(), this.load_skeleton_step.skeleton_type())

    // Pass head weight correction settings to the weight skin step
    this.weight_skin_step.set_head_weight_correction_settings(
      this.edit_skeleton_step.use_head_weight_correction(),
      this.edit_skeleton_step.get_preview_plane_height()
    )
  }

  public show_contributors_dialog (): void {
    new ModalDialog('Contributors', Generators.get_contributors_list()).show()
  }

  public show_learning_resources_dialog (): void {
    new ModalDialog('Learning Resources', Generators.get_learning_resources_html()).show()
  }
} // end Mesh2Motion Engine
